import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { History, PlusCircle, Settings, Trophy } from 'lucide-react'
import { useV1MeRetrieve, useV1RankingsChallengesList } from '@/api/client'
import type { ChallengeRead } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { GlobalConfigDialog } from '@/components/rankings/global-config-dialog'
import { ChallengeList } from '@/components/rankings/challenge-list'
import {
  Leaderboard,
  type LeaderboardLevel,
  type SelectedRanking,
} from '@/components/rankings/leaderboard'
import { ManualHistoryPanel } from '@/components/rankings/manual-history-panel'
import {
  ManualPointsDialog,
  type ManualEntryStudent,
  type ManualEntryTarget,
} from '@/components/rankings/manual-points-dialog'
import { StudentPickerDialog } from '@/components/rankings/student-picker-dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_panel/rankingi')({ component: RankingsPage })

type View = 'leaderboard' | 'challenges' | 'history'

const GLOBAL = 'global'

function RankingsPage() {
  const me = useV1MeRetrieve()
  const role = me.data?.status === 200 ? me.data.data.role : undefined
  const isOperator = role === 'superadmin' || role === 'admin'

  const [view, setView] = useState<View>('leaderboard')
  const [ranking, setRanking] = useState<SelectedRanking>({ kind: 'global' })
  const [level, setLevel] = useState<LeaderboardLevel>({ type: 'schools' })

  const [configOpen, setConfigOpen] = useState(false)
  const [studentPickerOpen, setStudentPickerOpen] = useState(false)
  const [manualStudent, setManualStudent] = useState<ManualEntryStudent | null>(null)

  // Challenge options for the ranking selector.
  const challengesQuery = useV1RankingsChallengesList()
  const challenges = challengesQuery.data?.status === 200 ? challengesQuery.data.data.results : []

  const selectRanking = (next: SelectedRanking) => {
    setRanking(next)
    setLevel({ type: 'schools' })
  }

  const openChallengeLeaderboard = (challenge: ChallengeRead) => {
    selectRanking({ kind: 'challenge', id: challenge.id })
    setView('leaderboard')
  }

  // Manual entry targets the currently selected ranking. For a challenge it must be
  // active (the backend refuses a finished/planned one with 409) — the button shows
  // only when the target is eligible.
  const activeChallengeIds = new Set(
    challenges.filter((c) => c.state === 'active').map((c) => c.id),
  )
  const manualTarget: ManualEntryTarget | null =
    ranking.kind === 'global'
      ? { kind: 'global' }
      : activeChallengeIds.has(ranking.id)
        ? { kind: 'challenge', id: ranking.id }
        : null

  const currentChallengeName =
    ranking.kind === 'challenge'
      ? (challenges.find((c) => c.id === ranking.id)?.name ?? 'Challenge')
      : null

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Trophy className="size-5" />
            Rankingi
          </span>
        }
        actions={
          isOperator && (
            <Button variant="outline" size="sm" onClick={() => setConfigOpen(true)}>
              <Settings /> Konfiguracja globalna
            </Button>
          )
        }
      />

      <div className="p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2">
            <ViewTab active={view === 'leaderboard'} onClick={() => setView('leaderboard')}>
              Tablica wyników
            </ViewTab>
            <ViewTab active={view === 'challenges'} onClick={() => setView('challenges')}>
              Challenge
            </ViewTab>
            {isOperator && (
              <ViewTab active={view === 'history'} onClick={() => setView('history')}>
                <History className="size-3.5" /> Historia wpisów
              </ViewTab>
            )}
          </div>

          {view === 'leaderboard' && (
            <div className="flex items-center gap-2">
              <Select
                value={ranking.kind === 'global' ? GLOBAL : String(ranking.id)}
                onValueChange={(v) =>
                  selectRanking(
                    v === GLOBAL ? { kind: 'global' } : { kind: 'challenge', id: Number(v) },
                  )
                }
              >
                <SelectTrigger size="sm" className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={GLOBAL}>Ranking globalny</SelectItem>
                  {challenges.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isOperator && manualTarget && (
                <Button size="sm" onClick={() => setStudentPickerOpen(true)}>
                  <PlusCircle /> Dodaj punkty ręcznie
                </Button>
              )}
            </div>
          )}
        </div>

        {view === 'leaderboard' && (
          <>
            {ranking.kind === 'challenge' && (
              <p className="mb-3 text-sm text-muted-foreground">
                Ranking challengu:{' '}
                <span className="font-medium text-foreground">{currentChallengeName}</span>
              </p>
            )}
            <Leaderboard ranking={ranking} level={level} onLevelChange={setLevel} />
          </>
        )}

        {view === 'challenges' && (
          <ChallengeList isOperator={isOperator} onOpenLeaderboard={openChallengeLeaderboard} />
        )}

        {view === 'history' && isOperator && <ManualHistoryPanel />}
      </div>

      {isOperator && (
        <>
          <GlobalConfigDialog open={configOpen} onOpenChange={setConfigOpen} />
          <StudentPickerDialog
            open={studentPickerOpen}
            onOpenChange={setStudentPickerOpen}
            onPick={setManualStudent}
          />
          {manualTarget && (
            <ManualPointsDialog
              target={manualTarget}
              student={manualStudent}
              onOpenChange={(open) => !open && setManualStudent(null)}
            />
          )}
        </>
      )}
    </>
  )
}

function ViewTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'border-transparent bg-primary text-primary-foreground'
          : 'bg-background text-muted-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  )
}
